import {
  InquiryListResponse, InquiryStageCounts, InquiryStage, Inquiry, InquiryDocument, InquiryFTF, InquiryPipelineFiltersUiModel, ViewingListMetadata
} from '../../../../types/inquiries';
import { ReqState, ListResponse, Listing } from '../../../../types';
import { Comment } from '../../../../types/comment.model';


////////////////////////////////////////////////////////////////////////////////
// Action name definitions
////////////////////////////////////////////////////////////////////////////////
const PREFIX = '[Inquiries]';

export const LOAD_ALL_STAGES = `${PREFIX} LOAD_ALL_STAGES`;
export const LOAD_STAGE = `${PREFIX} LOAD_STAGE`;
export const STORE_STAGE = `${PREFIX} STORE_STAGE`;
export const LOAD_STAGE_FAILED = `${PREFIX} LOAD_STAGE_FAILED`;
export const LOAD_MORE_FOR_STAGE = `${PREFIX} LOAD_MORE_FOR_STAGE`;

export const SELECT_STAGE = `${PREFIX} SELECT_STAGE`;
export const SEARCH = `${PREFIX} SEARCH`;
export const STORE_SEARCH = `${PREFIX} STORE_SEARCH`;

export const SHOW_MODAL = `${PREFIX} SHOW_MODAL`;
export const HIDE_MODAL = `${PREFIX} HIDE_MODAL`;

export const LOAD_NEXT_STAGE_PAGE = `${PREFIX} LOAD_NEXT_STAGE_PAGE`;
export const GO_TO_STAGE = `${PREFIX} GO_TO_STAGE`;

export const CLOSE_DETAILS = `${PREFIX} CLOSE_DETAILS`;
export const SET_FILTER_VISIBILITY = `${PREFIX} SET_FILTER_VISIBILITY`;
export const TOGGLE_FILTER_VISIBILITY = `${PREFIX} TOGGLE_FILTER_VISIBILITY`;

export const SELECT_INQUIRY = `${PREFIX} SELECT_INQUIRY`;

export const LOAD_FTF = `${PREFIX} LOAD_FTF`;
export const LOAD_COMMENTS = `${PREFIX} LOAD_COMMENTS`;
export const LOAD_SELECTED_PROPERTIES = `${PREFIX} LOAD_SELECTED_PROPERTIES`;
export const LOAD_VIEWING_LISTS = `${PREFIX} LOAD_VIEWING_LISTS`;
export const LOAD_OFFERS = `${PREFIX} LOAD_OFFERS`;
export const LOAD_CONTRACTS = `${PREFIX} LOAD_CONTRACTS`;

export const LOAD_FTF_FAILED = `${PREFIX} LOAD_FTF_FAILED`;
export const LOAD_COMMENTS_FAILED = `${PREFIX} LOAD_COMMENTS_FAILED`;
export const LOAD_SELECTED_PROPERTIES_FAILED = `${PREFIX} LOAD_SELECTED_PROPERTIES_FAILED`;
export const LOAD_VIEWING_LISTS_FAILED = `${PREFIX} LOAD_VIEWING_LISTS_FAILED`;
export const LOAD_OFFERS_FAILED = `${PREFIX} LOAD_OFFERS_FAILED`;
export const LOAD_CONTRACTS_FAILED = `${PREFIX} LOAD_CONTRACTS_FAILED`;

export const STORE_FTF = `${PREFIX} STORE_FTF`;
export const STORE_COMMENTS = `${PREFIX} STORE_COMMENTS`;
export const STORE_SELECTED_PROPERTIES = `${PREFIX} STORE_SELECTED_PROPERTIES`;
export const STORE_VIEWING_LISTS = `${PREFIX} STORE_VIEWING_LISTS`;
export const STORE_OFFERS = `${PREFIX} STORE_OFFERS`;
export const STORE_CONTRACTS = `${PREFIX} STORE_CONTRACTS`;

export const RELOAD_SELECTED_INQUIRY = `${PREFIX} RELOAD_SELECTED_INQUIRY`;
export const STORE_SELECTED_INQUIRY = `${PREFIX} STORE_SELECTED_INQUIRY`;

export const ATTACHMENTS_UPDATED_FOR_INQUIRY = `${PREFIX} ATTACHMENTS_UPDATED_FOR_INQUIRY`;


const INQUIRY_LOAD_ACTIONS = [
  LOAD_FTF,
  LOAD_COMMENTS,
  LOAD_SELECTED_PROPERTIES,
  LOAD_VIEWING_LISTS,
  LOAD_OFFERS,
  LOAD_CONTRACTS
];

////////////////////////////////////////////////////////////////////////////////
// State and action declarations
////////////////////////////////////////////////////////////////////////////////
export interface InquiriesState {
  // Core grid data
  stageInquiries?: { [key: string]: InquiryListResponse };
  reqStates?: { [key: string]: ReqState }
  stagePages?: { [key: string]: number };

  counts?: InquiryStageCounts;

  // Slider support
  currentSlideIndex?: number;

  // Search
  searchResults?: InquiryListResponse;
  searchString?: string;
  searchReqState?: ReqState;
  filters?: InquiryPipelineFiltersUiModel;

  // Modals and attachments support
  selectedInquiry?: Inquiry;
  visibleModal?: string;
  documentForEditing?: InquiryDocument;
  ftfForEditing?: InquiryFTF;
  selectedVlKey?: string;

  ftf?: InquiryFTF;
  comments?: ListResponse<Comment>;
  selectedProperties?: ListResponse<Listing>;
  viewingLists?: ListResponse<ViewingListMetadata>;
  offers?: ListResponse<InquiryDocument>;
  contracts?: ListResponse<InquiryDocument>;

  ftfReqState?: ReqState;
  commentsReqState?: ReqState;
  selectedPropertiesReqState?: ReqState;
  viewingListsReqState?: ReqState;
  offersReqState?: ReqState;
  contractsReqState?: ReqState;

  // Details panel support
  inquiryDetailsVisible?: boolean;
  filterCardVisible?: boolean;
}

export interface InquiriesAction {
  type: string;
  payload?: InquiriesState;

  stageSlug?: InquiryStage;
}

////////////////////////////////////////////////////////////////////////////////
// Action creation helpers
////////////////////////////////////////////////////////////////////////////////
export function loadAllStages(filters: InquiryPipelineFiltersUiModel = {}) {
  return {
    type: LOAD_ALL_STAGES,
    payload: { filters }
  } as InquiriesAction;
}

export function loadStage(stage: InquiryStage, filters: InquiryPipelineFiltersUiModel = {}) {
  return {
    type: LOAD_STAGE,
    stageSlug: stage,
    payload: { filters }
  } as InquiriesAction;
}

export function storeStage(stage: InquiryStage, inquiries: InquiryListResponse) {
  const action: InquiriesAction = {
    type: STORE_STAGE,
    stageSlug: stage
  };
  const state = {
    stageInquiries: {
      [stage]: inquiries
    },
    counts: inquiries.cards
  };

  action.payload = state;
  return action;
}

export function loadStageFailed(stage: InquiryStage) {
  
}

export function loadMoreForStage(stage: InquiryStage) {
  const action: InquiriesAction = {
    type: LOAD_MORE_FOR_STAGE,
    stageSlug: stage
  };

  return action;
}

export function selectStage(stage: InquiryStage) {
  return {
    type: SELECT_STAGE,
    stageSlug: stage
  } as InquiriesAction;
}

export function search(searchString: string, filters?: InquiryPipelineFiltersUiModel) {
  return {
    type: SEARCH,
    stageSlug: 'search',
    payload: { searchString, filters }
  } as InquiriesAction;
}

export function storeSearch(searchResults: InquiryListResponse) {
  return {
    type: STORE_SEARCH,
    payload: { searchResults }
  }
}


export function showModal(modalId: string, inqState: InquiriesState) {
  return {
    type: SHOW_MODAL,
    payload: { ...inqState, visibleModal: modalId }
  } as InquiriesAction;
}

export function hideModal() {
  return { type: HIDE_MODAL } as InquiriesAction;
}


export function selectInquiry(selectedInquiry: Inquiry) {
  return {
    type: SELECT_INQUIRY,
    payload: { selectedInquiry }
  } as InquiriesAction
}

export function closeDetailsCard() {
  return { type: CLOSE_DETAILS } as InquiriesAction;
}

export function setFilterVisibility(visible?: boolean) {
  return { type: SET_FILTER_VISIBILITY, payload: { filterCardVisible: visible } } as InquiriesAction;
}

export function toggleFilterVisibility() {
  return { type: TOGGLE_FILTER_VISIBILITY } as InquiriesAction;
}

export function createLoadActionsFor(selectedInquiry: Inquiry) {
  return INQUIRY_LOAD_ACTIONS.map(actionType => ({
    type: actionType,
    payload: { selectedInquiry }
  } as InquiriesAction));
}

export function storeFTF(inqState: InquiriesState) {
  return { type: STORE_FTF, payload: inqState } as InquiriesAction;
}

export function storeComments(inqState: InquiriesState) {
  return { type: STORE_COMMENTS, payload: inqState } as InquiriesAction;
}

export function storeSelectedProperties(inqState: InquiriesState) {
  return { type: STORE_SELECTED_PROPERTIES, payload: inqState } as InquiriesAction;
}

export function storeViewingLists(inqState: InquiriesState) {
  return { type: STORE_VIEWING_LISTS, payload: inqState }
}

export function storeOffers(inqState: InquiriesState) {
  return { type: STORE_OFFERS, payload: inqState } as InquiriesAction;
}

export function storeContracts(inqState: InquiriesState) {
  return { type: STORE_CONTRACTS, payload: inqState } as InquiriesAction;
}

export function attachmentUpdatedForInquiry(inquiry: Inquiry) {
  return { type: ATTACHMENTS_UPDATED_FOR_INQUIRY, payload: { selectedInquiry: inquiry } } as InquiriesAction;
}

export function reloadSelectedInquiry(selectedInquiry: Inquiry) {
  return { type: RELOAD_SELECTED_INQUIRY, payload: { selectedInquiry } } as InquiriesAction;
}

export function storeSelectedInquiry(selectedInquiry: Inquiry) {
  return { type: STORE_SELECTED_INQUIRY, payload: { selectedInquiry } } as InquiriesAction;
}
